import React, { Component, useState, useEffect } from "react";
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import axios from 'axios';
import sanitizeHtml from 'sanitize-html';
import { WordPressAPI } from '../../config';
import { Jumbotron, Hero } from '@/components/styles/JumbotronStyles';
import AuditFeaturesComponent from '@/components/AuditFeaturesComponent/AuditFeaturesComponent';
import LearnMoreBlogs from "@/components/LearnMoreBlogs";
import FooterComponent from '@/components/Footer/FooterComponent';
import { AuditDefenseStyles } from '@/components/AuditDefense/AuditDefenseStyles';
import DynamicButtonTTE from '@/components/DynamicButtonTTE/DynamicButtonTTE';
import Link from 'next/link';
import Header from '@/components/Header/Header';
import SEOHead from '@/components/SEOHead/SEOHead';
import validatePaidCampaign from '@helpers/validatePaidCampaign';
import Router from 'next/router';
import PullUpScheduler from "@components/PullUpScheduler/PullUpSchedulerV2";
import FaqComponent from '@/components/Bookkeeping/FaqComponent';

const TrustPilotComponent = dynamic(
  () => import('@/components/TrustPilot/TrustPilotComponent'),
  { ssr: false }
);

const AboutServiceComponent = dynamic(
  () => import('@components/AboutService/AboutServiceComponent'),
  { ssr: false }
);

const FooterNewsModal = dynamic(import('@/components/FooterNewsModal'));



const TPStyles = styled.div`
background-color: #F5F5F7;
padding: 20px 0px;
position: relative;

.tp-container {
  margin: 0 auto;
  max-width: 100%;
  
  ${props => props.theme.media.md} {
    margin-top: 10px;
    max-width: 67%;
  }
}
`;

//Unable to update in WP
const section_subtitle = "Learn about taxes, bookkeeping tips, and how to grow your small business.";

const scriptData = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [{
    "@type": "Question",
    "name": "What are the types of IRS audits?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "There are four types of audits the IRS might conduct as they look into your small business finances. The four types of audits include Correspondence, Office, Field, and the Taxpayer Compliance Measurement Program. Whichever type of audit you receive, you'll want the professionals at 1-800Accountant in your corner."
    }
  }, {
    "@type": "Question",
    "name": "Do I need an audit defense for my small business?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "There are numerous benefits to utilizing a robust audit defense. It gives you peace of mind and the ability to fight the IRS on even ground. 1-800Accountant's Audit Defense service ensures all of your income tax audit materials are in order and ready for IRS scrutiny."
    }
  }]
});

const AuditDefense = ({ data, blogModuleData }) => {
  const { title, acf } = data;
  const paid_campaign = validatePaidCampaign(acf.paid_campaign);
  const additional_questions = acf.additional_questions;
  const faqs = acf.q_and_a;
  const faqTitle = acf.faq_title;

  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [hideSchedulerHeader, setHideSchedulerHeader] = useState(false);

  useEffect(() => {
    if (isSchedulerOpen) {
      setHideSchedulerHeader(true);
    } else {
      setHideSchedulerHeader(false);
    }
  }, [isSchedulerOpen])

  const toggleIsSchedulerOpen = () => setIsSchedulerOpen(!isSchedulerOpen);

  const handleClick = () => {
    setIsSchedulerOpen(true);
  }

  return (
    <AuditDefenseStyles>
      <SEOHead>
        <title>{acf?.page_title_tag || title.rendered}</title>
        <meta
          name="description"
          content={acf?.meta_description || ''}
          key="description"
        />
        <script
          type="text/javascript"
          src="//app-ab33.marketo.com/js/forms2/js/forms2.min.js"
        />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: sanitizeHtml(scriptData) }} />
      </SEOHead>
      <Header
        AdditionalQuestions={additional_questions}
        PaidCampaign={typeof (paid_campaign) === 'undefined' ? 1 : paid_campaign}
        phone_number={acf.phone_number_setup}
        leadValues={acf.lead_values}
        hideSchedulerHeader={hideSchedulerHeader}
      />
      <Jumbotron background={data.acf.hero_background_image.url}>
        <div className="audit-defense-header">
          <Hero>
            <div className="container">
              <h1>{acf.hero_title}</h1>
              <div
                className="sub-header"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(data.acf.hero_sub_title, {
                    allowedTags: ['p', 'span', 'div'],
                  }),
                }}
              />
              <div className="row justify-content-md-center">
                <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6">

                  <button onClick={handleClick}
                    className={`btn btn-secondary btn-lg mx-auto`}
                  >
                    Talk to an Expert
                  </button>
                </div>
              </div>
            </div>
          </Hero>
        </div>
      </Jumbotron>
      <div className="trust-pilot-container">
        <TPStyles>
          <div className="tp-container">
            <TrustPilotComponent
              templateID="5406e65db0d04a09e042d5fc"
              height="75px"
              alt
            />
          </div>
        </TPStyles>
      </div>
      <AuditFeaturesComponent
        title={acf.section_title}
        subtitle={acf.section_subtitle}
        features={acf.section}

      />
      <section className="about-services">
        {acf.about_services.map((item, index) => (
          <AboutServiceComponent
            key={index}
            data={item}
            handleScheduler={handleClick}
            whiteBg
            showImgs
            customStyle={`audit-defense index-${index}`}
            wordPressVideo={false}

          />
        ))}
      </section>
      <section className="faq">
        <FaqComponent title={faqTitle} faqs={faqs} />
      </section>
      <section className="irs-communication">
        <div className="container">
          <div className="inner">
            <img src="/static/images/irs.svg" alt="irs" width="100px" height="100px" />
            <div className="content">
              <h3>IRS <br />Communication <span>Notice</span></h3>
              <p>The IRS will never contact you by phone, text message, or e-mail. Official communications are delivered exclusively via the USPS.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="learn-more-blogs">
        <LearnMoreBlogs
          blogModuleData={blogModuleData}
          section_subtitle={section_subtitle}
          wwo_section_title={acf.wwo_section_title}
        />
        <div className="text-center">
          <button onClick={handleClick}
            className={`btn btn-secondary btn-lg mx-auto`}
          >
            Schedule My Free Call
          </button>
        </div>
      </section>
      {/* Email Newsletter */}
      <FooterNewsModal />
      <FooterComponent phone_number={acf.phone_number_setup} />
      {
        isSchedulerOpen && (
          <PullUpScheduler
            setIsSchedulerOpen={toggleIsSchedulerOpen}
            AdditionalQuestions={additional_questions}
            leadValues={acf.lead_values}
            PaidCampaign={paid_campaign}
          />
        )
      }
    </AuditDefenseStyles >
  );
}

export const getStaticProps = async () => {
  const GET_POST_URL = `${process.env.NEXT_PUBLIC_BLOG}/wp-json/wp/v2/posts`;

  const axiosWP = axios.get(`${WordPressAPI.pages}/245`);
  const axiosBlog1 = axios.get(`${GET_POST_URL}?slug=you-received-an-audit-notice-now-what&_embed=true`);
  const axiosBlog2 = axios.get(`${GET_POST_URL}?slug=audit-defense-for-business&_embed=true`);
  const axiosBlog3 = axios.get(`${GET_POST_URL}?slug=common-causes-irs-audits&_embed=true`);

  const [{ data }, blogData1, blogData2, blogData3] = await Promise.all([axiosWP, axiosBlog1, axiosBlog2, axiosBlog3]);

  const post1 = blogData1.data[0];
  const post2 = blogData2.data[0];
  const post3 = blogData3.data[0];

  const blogModuleData = [post1, post2, post3];

  return {
    props: {
      data,
      blogModuleData
    },
    // revalidate: 1,
  };
};

export default AuditDefense;
